import { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "./ConfidenceSection.scss";
import Loader from "./Loader";
import { AuthContext } from "../../context/AuthContext";

const defaultCardsData = [
  {
    id: 1,
    category: <>Information<br/>Technology</>,
    change: "+0.8%",
    isPositive: true,
    title: <>Loading...</>,
    symbol: "WIPRO.NS",
    chartPath: "M0,25 L10,22 L20,24 L30,18 L40,20 L50,15 L60,18 L70,10 L80,12 L90,5 L100,5",
    chartFill: "M0,25 L10,22 L20,24 L30,18 L40,20 L50,15 L60,18 L70,10 L80,12 L90,5 L100,5 L100,30 L0,30 Z",
    chartColor: "#22c55e",
    mktCap: "N/A",
    w52: "N/A",
    vol: "N/A",
  },
];

const ConfidenceSection = () => {
  const [activeIndex, setActiveIndex] = useState(1);
  const [isNavigating, setIsNavigating] = useState(false);
  const [cardsData, setCardsData] = useState(defaultCardsData);
  const [isLoading, setIsLoading] = useState(true);
  const { userInfo } = useContext(AuthContext);
  const navigate = useNavigate();

  // Investment Ideas ("Select With Quantitative Methodology") lives at
  // /nifty-strategy, which is wrapped in ProtectedPageHOC (requiredScope:
  // "page:investmentideas").
  const INVESTMENT_IDEAS_PATH = "/nifty-strategy";

  const handleViewAllIdeas = () => {
    if (!userInfo) {
      // Not logged in → go to login, then return here after verifying.
      navigate("/login", { state: { from: INVESTMENT_IDEAS_PATH } });
      return;
    }
    // Logged in → open Investment Ideas. ProtectedPageHOC renders the page if
    // the user has the scope, otherwise it shows the "Request access" modal.
    navigate(INVESTMENT_IDEAS_PATH);
  };

  // Fetch stock data from API
  useEffect(() => {
    const fetchStockData = async () => {
      try {
        const baseUrl = process.env.REACT_APP_COMPANY_DOC;
        if (!baseUrl) {
          console.error("Missing REACT_APP_COMPANY_DOC environment variable");
          setIsLoading(false);
          return;
        }

        const url = `${baseUrl}/website_v2/stock_data.json`;
        console.log("[confidence] fetching from", url);
        
        const response = await axios.get(url);
        const stockDataArray = response?.data?.data || [];

        // Get 9 random stocks
        const shuffled = [...stockDataArray].sort(() => Math.random() - 0.5);
        const selectedStocks = shuffled.slice(0, 9);

        // Transform API data to card format
        const cards = selectedStocks.map((stock: any, index: number) => {
          const ltp = stock.ltp || 0;
          const prevClose = stock.prev_close || ltp;
          const dayChangePercent = prevClose !== 0 ? ((ltp - prevClose) / prevClose) * 100 : 0;
          const isPositive = dayChangePercent >= 0;
          
          // 52W percentage: ((ltp - fifty_two_week_low) / fifty_two_week_low) * 100
          const fiftyTwoWeekHigh = stock.fifty_two_week_high || ltp;
          const fiftyTwoWeekLow = stock.fifty_two_week_low || ltp;
          const fiftyTwoWeekPercent = fiftyTwoWeekLow !== 0 
            ? ((ltp - fiftyTwoWeekLow) / fiftyTwoWeekLow) * 100 
            : 0;
          
          // Format market cap to Cr
          const marketCapInCr = (stock.market_cap || 0) / 10000000;

          // Format volume
          const rawVolume = stock.volume || 0;
          const volumeInCr = rawVolume / 10000000;
          
          // Generate chart path data
          const chartPoints = [
            { x: 0, y: isPositive ? 25 : 5 },
            { x: 15, y: isPositive ? 20 : 8 },
            { x: 30, y: isPositive ? 18 : 12 },
            { x: 45, y: isPositive ? 12 : 15 },
            { x: 60, y: isPositive ? 15 : 20 },
            { x: 75, y: isPositive ? 8 : 22 },
            { x: 90, y: isPositive ? 5 : 25 },
            { x: 100, y: isPositive ? 2 : 28 },
          ];
          
          const chartPath = chartPoints.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ');
          const chartFill = chartPath + ' L100,30 L0,30 Z';
          
          return {
            id: index + 1,
            category: <>{stock.sector}</>,
            change: `${isPositive ? '+' : ''}${dayChangePercent.toFixed(2)}%`,
            isPositive: isPositive,
            title: <>{stock.company_name}</>,
            symbol: stock.ticker,
            chartPath: chartPath,
            chartFill: chartFill,
            chartColor: isPositive ? "#22c55e" : "#ef4444",
            mktCap: marketCapInCr > 0 ? `${marketCapInCr.toLocaleString("en-IN", { minimumFractionDigits: 1, maximumFractionDigits: 1 })} Cr` : "N/A",
            w52: `${fiftyTwoWeekPercent.toFixed(1)}%`,
            vol: rawVolume > 0
              ? volumeInCr >= 1
                ? `${volumeInCr.toFixed(2)} Cr`
                : `${(rawVolume / 100000).toFixed(2)} L`
              : "N/A",
          };
        });

        if (cards.length > 0) {
          setCardsData(cards);
          setActiveIndex(Math.min(1, cards.length - 1));
        }
        setIsLoading(false);
      } catch (error) {
        console.error("[confidence] fetch failed", error);
        setIsLoading(false);
      }
    };

    fetchStockData();
  }, []);

  // Auto-advance carousel every 4 seconds
  useEffect(() => {
    if (cardsData.length === 0) return;
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % cardsData.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [cardsData.length]);

  const nextCard = () => {
    setActiveIndex((prev) => (prev + 1) % cardsData.length);
  };

  const prevCard = () => {
    setActiveIndex((prev) => (prev - 1 + cardsData.length) % cardsData.length);
  };

  const getCardData = (offset: number) => {
    const index = (activeIndex + offset + cardsData.length) % cardsData.length;
    return cardsData[index];
  };

  const renderCard = (card: typeof cardsData[0], className: string) => (
    <div key={`${className}-${card.id}`} className={`stock-card ${className}`}>
      <div className="card-header">
        <div className="card-category">{card.category}</div>
        <div className={`card-change ${card.isPositive ? 'positive' : 'negative'}`}>{card.change}</div>
      </div>
      <div className="card-title">
        <h4>{card.title}</h4>
        <span className="card-symbol">{card.symbol}</span>
      </div>
      <div className="card-chart">
        <svg viewBox="0 0 100 30" preserveAspectRatio="none">
          <path d={card.chartPath} fill="none" stroke={card.chartColor} strokeWidth="2" vectorEffect="non-scaling-stroke" />
          <path d={card.chartFill} fill={card.isPositive ? "rgba(34, 197, 94, 0.1)" : "rgba(239, 68, 68, 0.1)"} stroke="none" />
        </svg>
      </div>
      <div className="card-metrics">
        <div className="metric">
          <span className="metric-label">Mkt Cap</span>
          <span className="metric-value">{card.mktCap}</span>
        </div>
        <div className="metric">
          <span className="metric-label">52W</span>
          <span className="metric-value">{card.w52}</span>
        </div>
        <div className="metric">
          <span className="metric-label">Vol</span>
          <span className="metric-value">{card.vol}</span>
        </div>
      </div>
      <div className="card-actions">
        <button
          className="action-btn primary"
          onClick={() => {
            setIsNavigating(true);
            window.localStorage.setItem("selectedTab", "0");
            window.localStorage.setItem("tickerId", card.symbol);
            setTimeout(() => { window.location.href = "/detail"; }, 4500);
          }}
        >Analyze</button>
      </div>
    </div>
  );

  return (
    <>
    {isNavigating && (
      <div style={{
        position: "fixed", inset: 0, zIndex: 99999,
        background: "rgba(255,255,255,0.85)",
        backdropFilter: "blur(6px)",
        display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
        gap: "1rem",
      }}>
        <Loader />
        <p style={{ fontFamily: "Inter, sans-serif", fontSize: "0.95rem", color: "#4b5563" }}>
          Loading stock analysis...
        </p>
      </div>
    )}
    {isLoading ? (
      <div style={{
        display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
        gap: "1rem", padding: "4rem 2rem",
      }}>
        <Loader />
        <p style={{ fontFamily: "Inter, sans-serif", fontSize: "0.95rem", color: "#4b5563" }}>
          Loading investment ideas...
        </p>
      </div>
    ) : (
    <div className="confidence-section">
      <div className="selection-section">
        <div className="badge">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="7" y1="17" x2="17" y2="7"></line><polyline points="7 7 17 7 17 17"></polyline></svg>
          INVESTMENT IDEAS
        </div>
        <div className="selection-title">
          <h1>Select With<br/>Quantitative Methodology</h1>
        </div>
        <p className="selection-description">
          Turn complex data into actionable alpha. Our engine doesn't just show you stocks; it provides mathematically backed strategies.
        </p>

        <div className="selection-content">
          <div className="selection-content-item">
            <div className="icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="20" x2="18" y2="10"></line>
                <line x1="12" y1="20" x2="12" y2="4"></line>
                <line x1="6" y1="20" x2="6" y2="14"></line>
              </svg>
            </div>
            <a className="links" href="/strong-stocks-home">
              <div>
                <h3>Smart Stock Picks</h3>
                <p>Daily curated portfolios powered by proprietary algorithms. We track everything from Momentum and Value Based Stocks to Portfolio Optimization.</p>
              </div>
            </a>
          </div>

          <div className="selection-content-item">
            <div className="icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="5"></circle>
                <line x1="12" y1="1" x2="12" y2="3"></line>
                <line x1="12" y1="21" x2="12" y2="23"></line>
                <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
                <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
                <line x1="1" y1="12" x2="3" y2="12"></line>
                <line x1="21" y1="12" x2="23" y2="12"></line>
                <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line>
                <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
              </svg>
            </div>
            <a className="links" href="/strong-stocks-home">
              <div>
                <h3>Nifty Future Insights</h3>
                <p>Receive daily actionable Nifty Directional Signal.</p>
              </div>
            </a>
          </div>
        </div>
        
        <button className="view-all-btn" onClick={handleViewAllIdeas}>
          View All Ideas <span>&rarr;</span>
        </button>
      </div>

      <div className="carousel-section">
        <div className="cards-container">
          {renderCard(getCardData(-1), "prev-card")}
          {renderCard(getCardData(0), "active-card")}
          {renderCard(getCardData(1), "next-card")}
        </div>

        <div className="carousel-controls">
          <button className="control-btn prev" onClick={prevCard}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>
          </button>
          <div className="carousel-dots">
            {cardsData.map((_, idx) => (
              <span 
                key={idx} 
                className={`dot ${idx === activeIndex ? "active-dot" : ""}`}
                onClick={() => setActiveIndex(idx)}
                style={{ cursor: "pointer" }}
              ></span>
            ))}
          </div>
          <button className="control-btn next" onClick={nextCard}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>
          </button>
        </div>
      </div>
    </div>
    )}
    </>
  );
};

export default ConfidenceSection;
